"use client";
import { FC, useState } from "react";
import { InteractiveTableDialog } from "./InteractiveTableDialog";
import { ComponentWithAnalyticsGlobalField } from "@solarwinds-webbiz/types";
import { Button } from "@/components/Button";

export const ICON_MAPPING = {
  checkmark: <span className="text-swdc-black-600 font-bold">&#x2713;</span>,
  star: <span className="text-swdc-orange-400 font-bold">&#x2605;</span>,
} as const;

export type TableControlSectionProps = {
  title: string | undefined;
  options: {
    icon: keyof typeof ICON_MAPPING;
    description: string;
  }[];
  clearButton: string;
  analytics?: {
    dlt: string;
    dld: string;
  };
  clearModal: {
    headline: string;
    description: string;
    backButtonText: string;
    backButtonAnalytics?: ComponentWithAnalyticsGlobalField;
    startNewButtonText: string;
    startNewButtonAnalytics?: ComponentWithAnalyticsGlobalField;
  };
  clearModel: () => void;
};

const TableControlSection: FC<TableControlSectionProps> = ({
  title,
  options,
  clearButton,
  clearModal,
  clearModel,
  analytics,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const closeDialog = () => {
    setIsOpen(false);
  };

  const enhancedClearModel = () => {
    clearModel();
    closeDialog();
  };

  return (
    <div className="flex items-center justify-center gap-12 py-2">
      <div className="flex items-center gap-6">
        <div className="text-subhead-xs text-swdc-black-600 font-bold">
          {title}
        </div>
        {options.map((item, optionIdx) => (
          <div
            key={`table-control-option-${item.description}-${optionIdx}`}
            className="text-body-l text-swdc-gray-400"
          >
            <span className="inline-block">{ICON_MAPPING[item.icon]}</span>
            <span className="pl-2">{item.description}</span>
          </div>
        ))}
      </div>
      <Button
        variant="tertiary"
        icon="none"
        className="itsm-clear text-subhead-xs font-bold underline hover:decoration-2 active:decoration-2"
        onClick={() => setIsOpen(true)}
        dld={analytics?.dld}
        dlt={analytics?.dlt}
      >
        {" "}
        {clearButton}{" "}
      </Button>
      <InteractiveTableDialog
        isOpen={isOpen}
        closeDialog={closeDialog}
        headline={clearModal.headline}
      >
        <div className="text-body-m text-swdc-gray-400 pt-6">
          {clearModal.description}
        </div>
        <div className="flex min-w-124 justify-between pt-10">
          <Button
            size="m"
            variant="secondary"
            onClick={closeDialog}
            dld={clearModal.backButtonAnalytics?.dld}
            dlt={clearModal.backButtonAnalytics?.dlt}
          >
            {clearModal.backButtonText}
          </Button>
          <Button
            variant="primary"
            onClick={enhancedClearModel}
            dld={clearModal.startNewButtonAnalytics?.dld}
            dlt={clearModal.startNewButtonAnalytics?.dlt}
          >
            {clearModal.startNewButtonText}
          </Button>
        </div>
      </InteractiveTableDialog>
    </div>
  );
};

export default TableControlSection;
